import { useBoardsQuery } from '@modules/board/hooks/useBoardsQuery';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  type MenuProps,
  Typography,
  styled,
} from '@mui/material';
import { forwardRef } from 'react';
import { ExplorerMenu } from 'src/components/explorer/shared/ExplorerMenu';
import { Iconify } from 'src/components/iconify';
import type { Table } from 'src/generated/graphql';
import { useLocales } from 'src/locales';
import { TablesExplorerItem } from './TablesExplorerItem';

type TablesExplorerProps = MenuProps & {
  selectTable: (table: Table) => void;
  onConfirm?: () => void;
  value: Table | null;
  loading?: boolean;
};

export const TablesExplorer = forwardRef<any, TablesExplorerProps>(
  (
    { selectTable, value, sx, open, anchorEl, onClose, onConfirm, loading },
    ref,
  ) => {
    const { boards, isBoardsLoading } = useBoardsQuery();
    const { translate } = useLocales();
    return (
      <ExplorerMenu
        ref={ref}
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Typography variant="caption" color="text.secondary">
          {translate('header.nav_vertical.board_tab.boards')}
        </Typography>

        {isBoardsLoading ? (
          <MenuItem>
            <ListItemIcon>
              <Iconify icon="eos-icons:loading" />
            </ListItemIcon>
            <ListItemText>{translate('share.loading')}</ListItemText>
          </MenuItem>
        ) : (
          <Box sx={{ ...sx, maxHeight: 300, overflow: 'auto', p: 0 }}>
            {boards.map((board) => (
              <TablesExplorerItem
                key={board._id}
                item={board}
                selected={value}
                onSelect={selectTable}
              />
            ))}
          </Box>
        )}
        {onConfirm && (
          <StyledFooterContainer>
            <LoadingButton
              variant="contained"
              size="small"
              disabled={!value}
              onClick={onConfirm}
              loading={!!loading}
              sx={{ mt: 1, mr: 1 }}
            >
              {translate('shared.labels.create_task')}
            </LoadingButton>
          </StyledFooterContainer>
        )}
      </ExplorerMenu>
    );
  },
);

const StyledFooterContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  mt: 1,
}));
